import { api } from './api-client';
import type {
  HomeData,
  KpiCardData,
  OrderRowData,
  ProgressFill,
  OrderStatus,
} from '@/hooks/useHomeData';

interface DashboardStats {
  employeeCount: number;
  pendingApprovals: number;
  projectCount: number;
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  todoTasks: number;
  taskCompletionRate: number;
}

interface RecentApproval {
  id: string;
  title?: string;
  status?: string;
  bizType?: string;
  applicantName?: string;
  createdAt?: string;
}

interface DashboardOverview {
  stats: DashboardStats;
  recentTasks: unknown[];
  recentApprovals: RecentApproval[];
}

const STATIC_QUICK_ACTIONS: HomeData['quickActions'] = [
  { id: 'new-order', icon: 'add_circle', labelKey: 'home.qa_new_order' },
  { id: 'scan', icon: 'qr_code_scanner', labelKey: 'home.qa_scan', to: '/fa-inventory' },
  { id: 'qc', icon: 'fact_check', labelKey: 'home.qa_qc' },
  { id: 'delivery', icon: 'local_shipping', labelKey: 'home.qa_delivery', iconTone: 'amber' },
  { id: 'inventory', icon: 'inventory_2', labelKey: 'home.qa_inventory', iconTone: 'amber' },
  { id: 'shifts', icon: 'schedule', labelKey: 'home.qa_shifts', iconTone: 'green' },
];

function initials(name: string | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function statusToProgress(status: string | undefined): {
  progress: number;
  fill: ProgressFill;
  status: OrderStatus;
} {
  if (status === 'APPROVED') return { progress: 100, fill: 'green', status: 'complete' };
  if (status === 'REJECTED' || status === 'CANCELLED') {
    return { progress: 100, fill: 'amber', status: 'pending' };
  }
  return { progress: 50, fill: 'blue', status: 'active' };
}

function formatShortDate(iso: string | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

function adaptKpis(s: DashboardStats): KpiCardData[] {
  return [
    {
      id: 'pending',
      labelKey: 'home.kpi_pending_approvals',
      value: String(s.pendingApprovals ?? 0),
      trendDir: 'up',
      trendText: '—',
      tone: 'warning',
    },
    {
      id: 'employees',
      labelKey: 'home.kpi_employees',
      value: String(s.employeeCount ?? 0),
      trendDir: 'up',
      trendText: '—',
      tone: 'primary',
    },
    {
      id: 'projects',
      labelKey: 'home.kpi_projects',
      value: String(s.projectCount ?? 0),
      trendDir: 'up',
      trendText: '—',
      tone: 'success',
    },
    {
      id: 'task-rate',
      labelKey: 'home.kpi_task_rate',
      value: String(Math.round(s.taskCompletionRate ?? 0)),
      unit: '%',
      trendDir: 'up',
      trendText: `${s.completedTasks}/${s.totalTasks}`,
      tone: 'secondary',
    },
  ];
}

function adaptRecent(items: RecentApproval[]): OrderRowData[] {
  return items.slice(0, 6).map((a) => {
    const prog = statusToProgress(a.status);
    return {
      id: a.id.slice(0, 10).toUpperCase(),
      customerInitials: initials(a.applicantName),
      client: `${a.applicantName ?? 'Unknown'} · ${a.title ?? a.bizType ?? ''}`,
      progress: prog.progress,
      fill: prog.fill,
      status: prog.status,
      date: formatShortDate(a.createdAt),
    };
  });
}

export async function fetchHomeData(): Promise<HomeData> {
  const { data } = await api.get<DashboardOverview>('/dashboard/overview');
  return {
    kpis: adaptKpis(data.stats),
    quickActions: STATIC_QUICK_ACTIONS,
    recentOrders: adaptRecent(data.recentApprovals ?? []),
  };
}
